import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Calendar, X } from "lucide-react";
import { api } from "../lib/api";

const DAYS = [
  { v: "vendredi", label: "Vendredi 1 Mai" },
  { v: "samedi", label: "Samedi 2 Mai" },
  { v: "dimanche", label: "Dimanche 3 Mai" },
];

const Galerie = () => {
  const [items, setItems] = useState(null);
  const [activeDay, setActiveDay] = useState("Tous");
  const [lightbox, setLightbox] = useState(null); // photo

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const grouped = useMemo(() => {
    const out = { vendredi: [], samedi: [], dimanche: [] };
    (items || []).forEach((p) => {
      if (out[p.day]) out[p.day].push(p);
    });
    return out;
  }, [items]);

  const visibleDays = activeDay === "Tous" ? DAYS : DAYS.filter((d) => d.v === activeDay);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="galerie-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Souvenirs du tournoi
          </span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-none">
            <span className="text-[#E60012]">Galerie</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Les meilleurs moments de la Coupe CH, jour par jour.
          </p>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {[{ v: "Tous", label: "Tous les jours" }, ...DAYS].map((d) => (
              <button
                key={d.v}
                data-testid={`gallery-filter-${d.v}`}
                onClick={() => setActiveDay(d.v)}
                className={`px-4 py-2 font-condensed uppercase tracking-[0.2em] text-xs transition-colors ${
                  activeDay === d.v
                    ? "bg-[#E60012] text-white"
                    : "border border-white/15 text-white/70 hover:border-[#E60012] hover:text-white"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 space-y-16">
        {items === null && (
          <div className="text-center py-20 text-white/50 font-condensed uppercase tracking-[0.2em]">
            Chargement…
          </div>
        )}
        {items !== null && items.length === 0 && (
          <div className="bg-[#111] border border-white/10 p-12 text-center">
            <Camera className="w-10 h-10 text-[#E60012] mx-auto mb-3" />
            <div className="font-display text-2xl uppercase">Galerie à venir</div>
            <p className="mt-2 text-white/55 text-sm">Les premières photos seront publiées dès le vendredi 1<sup>er</sup> mai.</p>
          </div>
        )}
        {items && visibleDays.map((d) => {
          const photos = grouped[d.v];
          if (!photos || photos.length === 0) return null;
          return (
            <div key={d.v} data-testid={`gallery-day-${d.v}`}>
              <div className="flex items-center gap-3 mb-5">
                <Calendar className="w-5 h-5 text-[#E60012]" />
                <h2 className="font-display text-3xl sm:text-4xl uppercase">{d.label}</h2>
                <span className="text-xs text-white/50 font-condensed uppercase tracking-wider">
                  {photos.length} photo{photos.length > 1 ? "s" : ""}
                </span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((p, i) => (
                  <motion.button
                    key={p.id}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    onClick={() => setLightbox(p)}
                    data-testid={`gallery-photo-${p.id}`}
                    className="relative aspect-square overflow-hidden border border-white/10 hover:border-[#E60012] group"
                  >
                    <img
                      src={p.image_base64}
                      alt={p.caption || ""}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {p.caption && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="text-xs font-condensed uppercase tracking-wider text-white truncate">{p.caption}</div>
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-black/70 hover:bg-[#E60012] flex items-center justify-center"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img src={lightbox.image_base64} alt="" className="max-w-full max-h-[88vh] object-contain" />
          {lightbox.caption && (
            <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 font-condensed uppercase tracking-[0.2em] text-sm">
              {lightbox.caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Galerie;
